// Copyright (c) Microsoft. All rights reserved.

import { Caption2, makeStyles, mergeClasses, shorthands, tokens } from '@fluentui/react-components';
import debug from 'debug';
import React from 'react';
import { Constants } from '../../Constants';
import { GetResponseOptions, useChat } from '../../libs/hooks/useChat';
import { AuthorRoles, IChatMessage } from '../../libs/models/ChatMessage';
import { useAppDispatch, useAppSelector } from '../../redux/app/hooks';
import { RootState } from '../../redux/app/store';
import { addMessageToConversationFromUser, setIsLoadingMessages } from '../../redux/features/conversations/conversationsSlice';
import { Breakpoints, SharedStyles } from '../../styles';
import { ChatGuidance } from './ChatGuidance';
import { ChatInput } from './ChatInput';
import { ChatHistory } from './chat-history/ChatHistory';
import { EffectivePromptsDialog } from './prompt-dialog/EffectivePromptsDialog';

const log = debug(Constants.debug.root).extend('chat-room');

const useClasses = makeStyles({
    root: {
        ...shorthands.overflow('hidden'),
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        height: '100%',
    },
    scroll: {
        ...shorthands.margin(tokens.spacingVerticalXS),
        ...SharedStyles.scroll,
    },
    history: {
        ...shorthands.padding(tokens.spacingVerticalM),
        marginLeft: '40px',
        ...Breakpoints.medium({
            marginLeft: '0',
        }),
        paddingRight: '40px',
        display: 'flex',
        justifyContent: 'center',
    },
    guidance: {
        display: 'block',
        ...Breakpoints.small({
            display: 'none',
        }),
    },
    input: {
        ...shorthands.padding(tokens.spacingVerticalM),
    },
    infomrationTextContainer: {
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: '100%',
        ...Breakpoints.medium({
            flexBasis: '70%',
        }),
        textAlign: 'center',
    },
    bottomContent: {
        display: 'flex',
        alignItems: 'center',
        columnGap: tokens.spacingHorizontalM,
        paddingRight: tokens.spacingHorizontalM,
        paddingBottom: '15px',
        '& button': {
            display: 'flex',
            flexGrow: 1,
            flexShrink: 0,
            flexBasis: 0,
        },
    },
});


interface ChatRoomProps {
    hiddenChat: boolean;
}

export const ChatRoom: React.FC<ChatRoomProps> = (props) => {
    const { conversations, selectedId, isLoadingMessages } = useAppSelector((state: RootState) => state.conversations);
    const { activeUserInfo } = useAppSelector((state: RootState) => state.app);

    const messages = conversations[selectedId].messages;

    const classes = useClasses();
    const dispatch = useAppDispatch();
    const scrollViewTargetRef = React.useRef<HTMLDivElement>(null);
    const [shouldAutoScroll, setShouldAutoScroll] = React.useState(true);

    const [isDraggingOver, setIsDraggingOver] = React.useState(false);
    const onDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDraggingOver(true);
    };
    const onDragLeave = (e: React.DragEvent<HTMLDivElement | HTMLTextAreaElement>) => {
        e.preventDefault();
        setIsDraggingOver(false);
    };

    const chat = useChat();

    React.useEffect(() => {
        if (!shouldAutoScroll) return;
        scrollViewTargetRef.current?.scrollTo(0, scrollViewTargetRef.current.scrollHeight);
    }, [messages, shouldAutoScroll]);


    React.useEffect(() => {
        const getMessages = async () => {
            dispatch(setIsLoadingMessages(true));
            const chatMessages: IChatMessage[] = await chat.getChatMessages(selectedId);
            messages.push(...chatMessages);
            dispatch(setIsLoadingMessages(false));
        };

        if (conversations[selectedId].messages.length === 0) {
            getMessages()
                .catch((e) => {
                    log('error getting messages', e);
                    dispatch(setIsLoadingMessages(false));
                });
        }

        setShouldAutoScroll(true);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedId]);

    React.useEffect(() => {

        const onScroll = () => {
            if (!scrollViewTargetRef.current) return;
            const { scrollTop, scrollHeight, clientHeight } = scrollViewTargetRef.current;
            const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10;
            setShouldAutoScroll(isAtBottom);
        };

        if (!scrollViewTargetRef.current) return;

        const currentScrollViewTarget = scrollViewTargetRef.current;

        currentScrollViewTarget.addEventListener('scroll', onScroll);
        return () => {
            currentScrollViewTarget.removeEventListener('scroll', onScroll);
        };

    }, []);

    const handleSubmit = async (options: GetResponseOptions) => {
        log('submitting user chat message');

        setShouldAutoScroll(true);

        const chatInput: IChatMessage = {
            chatId: selectedId,
            timestamp: new Date().getTime(),
            userId: activeUserInfo?.id as string,
            userName: activeUserInfo?.username as string,
            content: options.value,
            type: options.messageType,
            authorRole: AuthorRoles.User,
        };

        dispatch(addMessageToConversationFromUser({ message: chatInput, chatId: selectedId }));

        await chat.getResponse(options);

        // setShouldAutoScroll(true);
    };


    {
        return (
            <>
                {
                    props.hiddenChat ? (
                        <div className={classes.root}>
                            <div ref={scrollViewTargetRef} className={classes.scroll}>
                                <div className={classes.history}>
                                    <ChatHistory messages={messages} onGetResponse={handleSubmit} loadingMessages={isLoadingMessages} />
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className={classes.root} onDragEnter={onDragEnter} onDragOver={onDragEnter} onDragLeave={onDragLeave}>
                            <div ref={scrollViewTargetRef} className={classes.scroll}>
                                <div className={mergeClasses(classes.history, classes.guidance)}>
                                    <ChatGuidance onSubmit={handleSubmit} />
                                </div>
                                <div className={classes.history}>
                                    <ChatHistory messages={messages} onGetResponse={handleSubmit} loadingMessages={isLoadingMessages} />
                                </div>
                            </div>
                            <div className={classes.input}>
                                <ChatInput isDraggingOver={isDraggingOver} onDragLeave={onDragLeave} onSubmit={handleSubmit} />
                            </div>
                        </div>
                    )
                }
                <div className={classes.bottomContent}>
                    <div className={classes.infomrationTextContainer}>
                        <Caption2>Thomas is powered by Azure OpenAI and mistakes are possible. Please check important information.</Caption2>
                    </div>
                    <EffectivePromptsDialog />
                </div>
            </>
        )
    }
}